/**
 * Weather Data Explorer
 *
 * Filters are managed by CBE's built-in filter system (quickFilters).
 * CFFDRS toggle applies an analysis preset: hour=13 (1 PM NWT-local) + ffmc>0 +
 * sort by timestamp desc. CFFDRS columns are always visible from the schema;
 * users hide/show them via the standard column picker (#514).
 * Export CSV reads the last-used filter params captured by the service adapter wrapper.
 */
import React, { useState, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFire, faCloudSunRain } from '@fortawesome/free-solid-svg-icons';
import { useFusionAuth } from '@fusionauth/react-sdk';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import { ExportConfirmDialog } from '../baseExplorer/ExportConfirmDialog';
import { useExportConfirm } from '../baseExplorer/useExportConfirm';
import { downloadExportCsv } from '../baseExplorer/downloadExportCsv';
import { useMessages } from '../../common/AppMessages';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { weatherDataConfig } from './weatherDataConfig';
import { applyCffdrsPreset } from './applyCffdrsPreset';
import { appendWeatherFilters } from './weatherFilterParams';
import { WeatherDataRecord } from '../../../types/weatherData';
import { getBackendUrl } from '../../../services/configService';
import WeatherProductsPanel from './WeatherProductsPanel';
import { usePermissions } from '../../../context/PermissionContext';
import { useTools } from '../../../context/ToolContext';
import './WeatherDataExplorer.scss';

interface WeatherDataExplorerProps {
    className?: string;
    initialPosition?: { x: number; y: number };
    initialSize?: { width: number; height: number };
    onClose?: () => void;
    onSelectionChange?: (selection: any) => void;
    onDataChange?: () => void;
    isWindowed?: boolean;
    windowTitle?: string;
    resizable?: boolean;
    initialStationId?: string;
    initialStartDate?: string;
    initialEndDate?: string;
}

const WeatherDataExplorer: React.FC<WeatherDataExplorerProps> = (props) => {
    const { showMessage } = useMessages();
    const { isLoggedIn } = useFusionAuth();
    const { canDo } = usePermissions();
    const canViewCms = canDo('cms', 'read');
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const defaultPosition = isMobile ? { x: 5, y: 5 } : { x: 100, y: 100 };
    const defaultSize = isMobile
        ? { width: window.innerWidth - 10, height: window.innerHeight - 10 }
        : { width: 1200, height: 700 };

    // Weather Products coming soon modal
    const [showWeatherProducts, setShowWeatherProducts] = useState(false);

    // CFFDRS analysis preset toggle (filter/sort only; column visibility is
    // managed by the schema and the standard column picker — see #514).
    const [showCffdrs, setShowCffdrs] = useState(false);
    const cffdrsRef = useRef(false);

    // Refresh trigger for CFFDRS toggle (filters are managed by CBE internally)
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Store last-used filter params for export
    const lastApiParamsRef = useRef<URLSearchParams>(new URLSearchParams());

    // Wrap config to: (1) inject CFFDRS overrides, (2) capture filter params for export
    const config = useMemo(() => {
        const originalFetchData = weatherDataConfig.serviceAdapter.fetchData;
        return {
            ...weatherDataConfig,
            serviceAdapter: {
                ...weatherDataConfig.serviceAdapter,
                fetchData: async (params: any) => {
                    // CFFDRS preset: hour=13 (NWT-local), ffmc>0, sort timestamp desc.
                    // Hour column is computed in America/Yellowknife (DST-aware) —
                    // see weatherDataNormalizer.localHourYellowknife.
                    const modifiedParams = applyCffdrsPreset(params, cffdrsRef.current);
                    const filters = modifiedParams.filters ?? [];

                    // Capture filter values for export using the SAME mapping the
                    // list adapter uses, so the export cannot silently drop a
                    // constraint the table applied (#693).
                    const exportParams = new URLSearchParams();
                    appendWeatherFilters(exportParams, filters);
                    if (params.search?.term) {
                        exportParams.append('search', params.search.term);
                    }
                    lastApiParamsRef.current = exportParams;

                    return originalFetchData(modifiedParams);
                },
            },
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCffdrsToggle = useCallback(() => {
        setShowCffdrs(prev => {
            cffdrsRef.current = !prev;
            return !prev;
        });
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Snapshot the URL params at the moment the user clicks Export so the
    // download runs against the same filter set the count was based on.
    const exportParamsRef = useRef<string | null>(null);

    const buildBaseUrl = useCallback(() => {
        const backendUrl = getBackendUrl();
        return backendUrl ? `${backendUrl}/api/weather-data` : '/api/weather-data';
    }, []);

    const buildAuthHeaders = useCallback((): HeadersInit => {
        const headers: HeadersInit = {};
        const token = localStorage.getItem('access_token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return headers;
    }, []);

    // Same shared count-then-confirm flow CBE uses; only the URLs differ
    // because Weather Data hits its own /api/weather-data/export endpoint.
    const { handleExport, dialogProps: exportDialogProps } = useExportConfirm({
        fetchCount: useCallback(async () => {
            const params = lastApiParamsRef.current.toString();
            exportParamsRef.current = params;
            const res = await fetch(`${buildBaseUrl()}/export/count?${params}`, {
                headers: buildAuthHeaders(),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Network error while counting rows.');
            const data = await res.json();
            return { count: data.count, large: !!data.large };
        }, [buildBaseUrl, buildAuthHeaders]),

        performExport: useCallback(() => {
            const params = exportParamsRef.current;
            if (params == null) return;
            void downloadExportCsv({
                url: `${buildBaseUrl()}/export?${params}`,
                filename: 'weather-data',
                headers: buildAuthHeaders(),
                onMessage: showMessage,
            });
        }, [buildBaseUrl, buildAuthHeaders, showMessage]),
    });

    const toolbar = (
        <>
            <button
                type="button"
                className="btn-primary"
                onClick={handleExport}
                title="Export CSV"
                aria-label="Export CSV"
            >
                <FontAwesomeIcon icon={faDownload} />
                <span className="btn-label">Export CSV</span>
            </button>
            <button
                type="button"
                className={`wde-cffdrs-toggle btn-secondary${showCffdrs ? ' active' : ''}`}
                onClick={handleCffdrsToggle}
                title={showCffdrs
                    ? 'Clear CFFDRS analysis filter'
                    : 'Apply CFFDRS analysis filter (hour 13, FFMC > 0, latest first)'}
                aria-label={showCffdrs
                    ? 'Clear CFFDRS analysis filter'
                    : 'Apply CFFDRS analysis filter'}
            >
                <FontAwesomeIcon icon={faFire} />
                <span className="btn-label">CFFDRS</span>
            </button>
            {canViewCms && (
                <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowWeatherProducts(true)}
                    title="Weather Products"
                    aria-label="Weather Products"
                >
                    <FontAwesomeIcon icon={faCloudSunRain} />
                    <span className="btn-label">Weather Products</span>
                </button>
            )}
        </>
    );

    const { openTool } = useTools();

    // Per-row "View Station" handler (#577). Mirrors the map's weather-station
    // popup → WSE bridge: dispatch the wse:open-detail event for the case
    // where WSE is already open, and stash __wsePendingDetail for the case
    // where WSE has to mount first, then open the weatherStations tool.
    const handleViewStation = useCallback((row: WeatherDataRecord) => {
        const nesdis = row.stationId;
        if (!nesdis) return;
        (window as any).__wsePendingDetail = { nesdis };
        window.dispatchEvent(new CustomEvent('wse:open-detail', { detail: { nesdis } }));
        openTool('weatherStations');
    }, [openTool]);

    return (
        <div className="weather-data-explorer">
            <ConfigurableBaseExplorer<WeatherDataRecord>
                config={config}
                isAuthenticated={isLoggedIn}
                refreshTrigger={refreshTrigger}
                isWindowed={props.isWindowed ?? true}
                initialPosition={props.initialPosition ?? defaultPosition}
                initialSize={props.initialSize ?? defaultSize}
                resizable={props.resizable ?? true}
                draggable={true}
                showCloseButton={true}
                showMinimizeButton={true}
                windowTitle={props.windowTitle ?? 'Weather Data Explorer'}
                onWindowClose={props.onClose}
                onEntityView={handleViewStation}
                customToolbar={toolbar}
                help={<ContextHelp title='Help with Weather Data' context='weather-data-help' />}
                initialFilters={(() => {
                    const filters: Record<string, any> = {};
                    if (props.initialStationId) filters.stationId = props.initialStationId;
                    if (props.initialStartDate || props.initialEndDate) {
                        filters.dateRange = { from: props.initialStartDate, to: props.initialEndDate };
                    }
                    return Object.keys(filters).length > 0 ? filters : undefined;
                })()}
            />
            {showWeatherProducts && canViewCms && (
                <WeatherProductsPanel onClose={() => setShowWeatherProducts(false)} />
            )}
            <ExportConfirmDialog
                {...exportDialogProps}
                entityLabel={config.presentation?.title?.toLowerCase()}
                title={`${config.presentation?.title ?? ''} Export`.trim()}
            />
        </div>
    );
};

export default WeatherDataExplorer;
